import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { AppState } from 'src/app/app.reducer';
import {
  DestinoRemesa,
  PerfilUsuario,
} from '../remittance-hub/models/remittance.model';
import * as R from '../remittance-hub/rules/remittance-rules';
import {
  BeneficiaryBookService,
  CapacidadesLibreta,
  FilaBeneficiario,
  TabLibreta,
} from './beneficiary-book.service';

/**
 * LIBRETA ÚNICA DE BENEFICIARIOS.
 *
 * Un tab por país al que el perfil puede enviar, más el suyo propio cuando
 * ahí también tiene beneficiarios —el venezolano ve Venezuela, porque es la
 * libreta con la que retira su saldo en Bs; el chileno no ve Chile, porque sus
 * cuentas chilenas son las de retiro y se administran en otra pantalla— y en
 * cada tab su propio botón para registrar, con el método que ese país usa.
 *
 * Por debajo hay TRES libretas distintas (`CuentaBeneficiario`, `CuentaRetiro`,
 * `VitaBeneficiario`), con tres servicios y tres formularios. El componente no
 * conoce ninguna: le pide filas y capacidades a `BeneficiaryBookService`.
 *
 * NO toca backend y NO modifica las pantallas vigentes: `/recipients` seguía
 * siendo además un componente embebido (`single-money-order`), así que esta
 * vista es un componente nuevo al que apunta la ruta.
 */
@Component({
  selector: 'app-beneficiary-book',
  templateUrl: './beneficiary-book.component.html',
  styleUrls: ['./beneficiary-book.component.sass'],
})
export class BeneficiaryBookComponent implements OnInit, OnDestroy {
  private sub = new Subscription();

  user: any = null;
  perfil: PerfilUsuario = 'CL';

  tabs: TabLibreta[] = [];
  activo: DestinoRemesa | null = null;

  /** Filas del tab activo, ya filtradas por el buscador. */
  filas: FilaBeneficiario[] = [];
  private todas: FilaBeneficiario[] = [];

  cargando = false;
  error: string | null = null;
  busqueda = '';

  /** Países Vita activos. Gatea el registro, nunca el listado. */
  private vitaActivos: Set<string> | null = null;

  /** Alta en curso: cubre el ciclo completo, no sólo el diálogo. */
  registrando = false;
  /** Id de la fila con una acción en vuelo (ficha, edición, baja). */
  accionEnCurso: string | number | null = null;

  constructor(
    private store: Store<AppState>,
    private book: BeneficiaryBookService,
  ) {}

  ngOnInit(): void {
    this.sub.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        if (!user) return;
        const perfilNuevo = R.perfilDeTipoId(user?.tipoId);
        const primeraCarga = this.user === null;
        this.user = user;

        // El store re-emite con cualquier acción de `auth`: los tabs sólo se
        // rearman si cambió el perfil, para no reiniciar el tab elegido.
        if (primeraCarga || perfilNuevo !== this.perfil) {
          this.perfil = perfilNuevo;
          this.tabs = this.book.tabsDe(this.perfil);
          this.activo = this.tabs[0]?.pais ?? null;
          this.cargar();
        }
      }),
    );

    this.cargarPaisesVita();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private async cargarPaisesVita(): Promise<void> {
    this.vitaActivos = await this.book.paisesVitaActivos();
  }

  // ───────────────────── Tabs ─────────────────────

  seleccionarTab(pais: DestinoRemesa): void {
    if (this.activo === pais || this.cargando) return;
    this.activo = pais;
    this.busqueda = '';
    this.cargar();
  }

  get tabActivo(): TabLibreta | null {
    return this.tabs.find((t) => t.pais === this.activo) ?? null;
  }

  get capacidades(): CapacidadesLibreta | null {
    return this.activo ? this.book.capacidades(this.activo) : null;
  }

  /**
   * El registro de un país Vita necesita que el backend lo tenga activo: sin
   * variantes no hay formulario. El LISTADO no se gatea — ocultarle al cliente
   * beneficiarios que él cargó sería peor que mostrar un destino inactivo.
   */
  get puedeRegistrar(): boolean {
    if (!this.activo || this.registrando) return false;
    if (!R.esRielPropio(this.activo)) {
      return this.vitaActivos?.has(this.activo) === true;
    }
    return true;
  }

  get avisoDestinoInactivo(): boolean {
    return (
      !!this.activo &&
      !R.esRielPropio(this.activo) &&
      this.vitaActivos !== null &&
      !this.vitaActivos.has(this.activo)
    );
  }

  /** Etiqueta del botón: nombra el país, que es lo que va a registrar. */
  get labelRegistrar(): string {
    const t = this.tabActivo;
    return t ? `Registrar beneficiario en ${t.nombre}` : 'Registrar beneficiario';
  }

  /** Cuántos hay por tab, para el contador del propio tab. */
  conteo(pais: DestinoRemesa): number | null {
    return pais === this.activo ? this.todas.length : null;
  }

  /** Total del tab, sin el filtro del buscador. */
  get total(): number {
    return this.todas.length;
  }

  // ───────────────────── Listado ─────────────────────

  async cargar(): Promise<void> {
    if (!this.activo || !this.user?.usuarioId) return;

    const pedido = this.activo;
    this.cargando = true;
    this.error = null;
    try {
      const filas = await this.book.listar(pedido, Number(this.user.usuarioId));
      // El usuario pudo cambiar de tab mientras se cargaba.
      if (this.activo !== pedido) return;
      this.todas = filas;
      this.aplicarBusqueda();
    } catch (e: any) {
      if (this.activo !== pedido) return;
      this.todas = [];
      this.filas = [];
      this.error =
        typeof e === 'string'
          ? e
          : 'No pudimos cargar tus beneficiarios. Intentá de nuevo.';
    } finally {
      if (this.activo === pedido) this.cargando = false;
    }
  }

  onBusquedaChange(): void {
    this.aplicarBusqueda();
  }

  limpiarBusqueda(): void {
    this.busqueda = '';
    this.aplicarBusqueda();
  }

  /**
   * Se recalcula en un método y no en un getter: un getter devuelve un array
   * nuevo en cada ciclo de detección de cambios y re-renderiza la lista entera.
   */
  private aplicarBusqueda(): void {
    const q = this.busqueda.trim().toLowerCase();
    const base = q ? this.todas.filter((f) => f.busqueda.includes(q)) : [...this.todas];
    this.filas = base.sort((a, b) => a.titulo.localeCompare(b.titulo));
  }

  get vacio(): boolean {
    return !this.cargando && !this.error && this.todas.length === 0;
  }

  get sinResultados(): boolean {
    return (
      !this.cargando &&
      !this.error &&
      this.todas.length > 0 &&
      this.filas.length === 0
    );
  }

  trackById(_i: number, f: FilaBeneficiario): string {
    return `${f.libreta}:${f.id}`;
  }

  // ───────────────────── Acciones ─────────────────────

  /**
   * Un click es uno. `registrando` cubre el ciclo completo —el selector de
   * método, el formulario y la recarga—, no sólo el diálogo: el backdrop no
   * alcanza porque con Vita hay que pedir el schema antes de abrirlo.
   */
  async registrar(): Promise<void> {
    if (!this.activo || !this.puedeRegistrar) return;
    this.registrando = true;
    try {
      const guardado = await this.book.registrar(this.activo, this.perfil);
      if (guardado) await this.cargar();
    } catch {
      this.error = 'No pudimos abrir el formulario. Intentá nuevamente.';
    } finally {
      this.registrando = false;
    }
  }

  async editar(f: FilaBeneficiario): Promise<void> {
    if (this.accionEnCurso !== null || f.bloqueado) return;
    this.accionEnCurso = f.id;
    try {
      const guardado = await this.book.editar(f, this.perfil);
      if (guardado) await this.cargar();
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'No se puede editar ahora',
        text: 'No pudimos cargar el formulario de este beneficiario. Intentá más tarde.',
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.accionEnCurso = null;
    }
  }

  async verFicha(f: FilaBeneficiario): Promise<void> {
    if (this.accionEnCurso !== null) return;
    this.accionEnCurso = f.id;
    try {
      await this.book.verFicha(f);
    } finally {
      this.accionEnCurso = null;
    }
  }

  async eliminar(f: FilaBeneficiario): Promise<void> {
    if (this.accionEnCurso !== null) return;

    const confirmar = await Swal.fire({
      title: '¿Eliminar beneficiario?',
      text: `${f.titulo} dejará de aparecer en tu libreta. Los envíos que ya le hiciste no se modifican.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });
    if (!confirmar.isConfirmed) return;

    this.accionEnCurso = f.id;
    try {
      await this.book.eliminar(f);
      await this.cargar();
    } catch (e: any) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo eliminar',
        text: typeof e === 'string' ? e : 'Intentá de nuevo en unos minutos.',
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.accionEnCurso = null;
    }
  }

  ocupada(f: FilaBeneficiario): boolean {
    return this.accionEnCurso === f.id;
  }
}
